import type { SupabaseClient } from "@supabase/supabase-js";
import type {
  Asset,
  AssetDraft,
  AssetStatus,
  AssetType,
  ClientOption,
  ServiceSchedule,
  ServiceType,
} from "../types";

type DatabaseService = {
  last_service_date?: string | null;
  next_service_date?: string | null;
  last_service_label?: string | null;
  next_service_label?: string | null;
};

type RegisterRow = {
  id: string;
  asset_code: string;
  asset_type: "life_raft" | "life_boat";
  asset_name: string;
  platform_name: string;
  capacity: number | null;
  unit_count: number | null;
  manufacturer: string | null;
  brand: string | null;
  model: string | null;
  serial_number: string | null;
  imo_number: string | null;
  asset_location: string;
  year_of_manufacture: number | null;
  status: "operational" | "due_soon" | "under_service" | "expired";
  notes: string | null;
  client_id: string;
  client_code: string;
  client_name: string;
  service_schedule: Partial<Record<ServiceType, DatabaseService>> | null;
};

const STATUS_FROM_DATABASE: Record<RegisterRow["status"], AssetStatus> = {
  operational: "Operational",
  due_soon: "Due Soon",
  under_service: "Under Service",
  expired: "Expired",
};

const STATUS_TO_DATABASE: Record<AssetStatus, RegisterRow["status"]> = {
  Operational: "operational",
  "Due Soon": "due_soon",
  "Under Service": "under_service",
  Expired: "expired",
};

const TYPE_FROM_DATABASE: Record<RegisterRow["asset_type"], AssetType> = {
  life_raft: "Life Raft",
  life_boat: "Life Boat",
};

const TYPE_TO_DATABASE: Record<AssetType, RegisterRow["asset_type"]> = {
  "Life Raft": "life_raft",
  "Life Boat": "life_boat",
};

export const EDITABLE_SERVICES: Record<AssetType, ServiceType[]> = {
  "Life Raft": ["life_raft_service"],
  "Life Boat": [
    "annual_inspection",
    "five_yearly",
    "refurbishment",
    "hooks_wire_davit_winch",
  ],
};

function mapServices(schedule: RegisterRow["service_schedule"]): Asset["services"] {
  const mapped: Asset["services"] = {};
  if (!schedule) return mapped;

  Object.entries(schedule).forEach(([key, value]) => {
    if (!value) return;
    mapped[key as ServiceType] = {
      lastService: value.last_service_date ?? "",
      nextService: value.next_service_date ?? "",
      lastServiceLabel: value.last_service_label ?? undefined,
      nextServiceLabel: value.next_service_label ?? undefined,
    };
  });
  return mapped;
}

function mapAsset(row: RegisterRow): Asset {
  return {
    id: row.id,
    assetId: row.asset_code,
    type: TYPE_FROM_DATABASE[row.asset_type],
    clientId: row.client_id,
    clientCode: row.client_code,
    clientName: row.client_name,
    name: row.asset_name,
    vessel: row.platform_name,
    capacity: row.capacity,
    unitCount: row.unit_count ?? 1,
    manufacturer: row.manufacturer || row.brand || "",
    serial: row.serial_number ?? "",
    imoNumber: row.imo_number ?? "",
    model: row.model ?? "",
    location: row.asset_location,
    yearOfManufacture: row.year_of_manufacture,
    status: STATUS_FROM_DATABASE[row.status],
    services: mapServices(row.service_schedule),
    notes: row.notes ?? "",
  };
}

export async function fetchClients(
  supabase: SupabaseClient,
): Promise<ClientOption[]> {
  const { data, error } = await supabase
    .from("intrax_ss_clients")
    .select("id, client_code, client_name")
    .eq("status", "active")
    .order("client_name");

  if (error) throw error;
  return (data ?? []).map((client) => ({
    id: String(client.id),
    code: String(client.client_code),
    name: String(client.client_name),
  }));
}

export async function fetchAssets(
  supabase: SupabaseClient,
): Promise<Asset[]> {
  const { data, error } = await supabase
    .from("intrax_ss_asset_register")
    .select("*")
    .order("client_name")
    .order("platform_name")
    .order("asset_code");

  if (error) throw error;
  return ((data ?? []) as RegisterRow[]).map(mapAsset);
}

export async function saveAsset(
  supabase: SupabaseClient,
  asset: Asset | AssetDraft,
): Promise<Asset> {
  const { data: authData, error: authError } = await supabase.auth.getUser();
  if (authError) throw authError;
  if (!authData.user) {
    throw new Error("You must be signed in to save a record.");
  }

  const mainServiceType: ServiceType =
    asset.type === "Life Raft" ? "life_raft_service" : "annual_inspection";
  const mainService = asset.services[mainServiceType];
  const payload = {
    asset_code: asset.assetId,
    client_id: asset.clientId,
    asset_type: TYPE_TO_DATABASE[asset.type],
    asset_name: asset.name,
    vessel_name: asset.vessel,
    capacity: asset.capacity,
    unit_count: asset.unitCount,
    manufacturer: asset.manufacturer,
    brand: asset.manufacturer || null,
    model: asset.model || null,
    serial_number: asset.serial || null,
    imo_number: asset.imoNumber || null,
    asset_location: asset.location || asset.vessel,
    year_of_manufacture: asset.yearOfManufacture,
    last_service_date: mainService?.lastService || null,
    next_service_date: mainService?.nextService || null,
    status: STATUS_TO_DATABASE[asset.status],
    notes: asset.notes || null,
    updated_by: authData.user.id,
  };

  const result =
    "id" in asset
      ? await supabase
          .from("intrax_ss_assets")
          .update(payload)
          .eq("id", asset.id)
          .select("id")
          .single()
      : await supabase
          .from("intrax_ss_assets")
          .insert({ ...payload, created_by: authData.user.id })
          .select("id")
          .single();

  if (result.error) throw result.error;
  const savedAssetId = String(result.data.id);
  const editableServiceTypes = EDITABLE_SERVICES[asset.type];
  const rows = editableServiceTypes.flatMap((serviceType) => {
    const service: ServiceSchedule | undefined = asset.services[serviceType];
    if (!service?.lastService && !service?.nextService) return [];
    return [
      {
        asset_id: savedAssetId,
        service_type: serviceType,
        last_service_date: service.lastService || null,
        next_service_date: service.nextService || null,
        last_service_label: service.lastServiceLabel || null,
        next_service_label: service.nextServiceLabel || null,
        date_precision: "day",
        updated_at: new Date().toISOString(),
      },
    ];
  });

  const emptyServiceTypes = editableServiceTypes.filter((serviceType) => {
    const service = asset.services[serviceType];
    return !service?.lastService && !service?.nextService;
  });

  if (rows.length) {
    const { error } = await supabase
      .from("intrax_ss_asset_services")
      .upsert(rows, { onConflict: "asset_id,service_type" });
    if (error) throw error;
  }

  if (emptyServiceTypes.length) {
    const { error } = await supabase
      .from("intrax_ss_asset_services")
      .delete()
      .eq("asset_id", savedAssetId)
      .in("service_type", emptyServiceTypes);
    if (error) throw error;
  }

  await supabase.from("intrax_ss_activity_log").insert({
    user_id: authData.user.id,
    action: "asset_saved",
    asset_id: savedAssetId,
    details: { asset_code: asset.assetId, client_id: asset.clientId },
  });

  const { data, error } = await supabase
    .from("intrax_ss_asset_register")
    .select("*")
    .eq("id", savedAssetId)
    .single();
  if (error) throw error;
  return mapAsset(data as RegisterRow);
}

export async function deleteAsset(
  supabase: SupabaseClient,
  asset: Asset,
): Promise<void> {
  const { error } = await supabase
    .from("intrax_ss_assets")
    .delete()
    .eq("id", asset.id);
  if (error) throw error;
}
